import { createGlobalStyle } from 'styled-components';

const GlobalStyles = createGlobalStyle`
  :root {
    --color-espresso: #4E350F;
    --color-bronze: #5D4022;
    --color-silver: #BBBFBE;
    --color-royal: #0018CE;
    --color-periwinkle: #82ACD8;
    --color-ink: #211A12;
    --color-paper: #F7F7F3;
    --color-mist: #ECEFEB;
    --color-line: #DDDAD3;

    --color-background: var(--color-paper);
    --color-background-secondary: var(--color-mist);
    --color-surface: #ffffff;
    --color-text: var(--color-ink);
    --color-text-secondary: var(--color-bronze);
    --color-text-light: rgba(93, 64, 34, 0.72);
    --color-text-muted: rgba(93, 64, 34, 0.58);
    --color-border: var(--color-line);
    --color-border-light: rgba(221, 218, 211, 0.72);
    --color-primary: var(--color-espresso);
    --color-primary-light: var(--color-mist);
    --color-primary-lighter: var(--color-paper);
    --color-primary-dark: var(--color-ink);
    --color-accent: var(--color-royal);
    --color-accent-light: rgba(130, 172, 216, 0.16);
    --color-accent-dark: #0012A3;
    --color-error: #B42318;
    --color-error-dark: #8F1D14;
    --color-success: #047857;
    --color-warning: #B45309;
    --color-warning-dark: #8C4A08;
    --color-info: var(--color-periwinkle);
    
    --space-xxs: 0.25rem;
    --space-xs: 0.5rem;
    --space-sm: 1rem;
    --space-md: 2rem;
    --space-lg: 3rem;
    --space-xl: 4rem;
    
    --font-size-xs: 0.75rem;
    --font-size-sm: 0.875rem;
    --font-size-md: 1rem;
    --font-size-lg: 1.25rem;
    --font-size-xl: 1.5rem;
    --font-size-xxl: 2rem;
    --font-size-2xl: 2.25rem;
    --font-size-3xl: 2.5rem;
    --font-size-4xl: 3rem;
    
    --font-weight-light: 300;
    --font-weight-normal: 400;
    --font-weight-medium: 500;
    --font-weight-semibold: 600;
    --font-weight-bold: 700;
    
    --border-radius-sm: 4px;
    --border-radius-md: 8px;
    --border-radius-lg: 8px;
    
    --transition-speed: 0.2s;
    --shadow-small: 0 10px 30px rgba(33, 26, 18, 0.06);
    --shadow-medium: 0 18px 48px rgba(33, 26, 18, 0.08);
    --shadow-large: 0 24px 70px rgba(33, 26, 18, 0.16);

    --font-sans: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    --font-serif: 'Playfair Display', Georgia, 'Times New Roman', serif;
  }

  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  html, body {
    font-family: var(--font-sans);
    font-size: 16px;
    line-height: 1.5;
    color: var(--color-text);
    background-color: var(--color-background);
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  body {
    min-height: 100vh;
  }

  h1, h2, h3, h4, h5, h6 {
    margin-bottom: var(--space-sm);
    font-weight: var(--font-weight-bold);
    line-height: 1.2;
    letter-spacing: 0;
  }

  h1 {
    font-family: var(--font-serif);
    font-size: 2.5rem;
    font-weight: var(--font-weight-semibold);
    margin-bottom: var(--space-md);
    line-height: 1.2;
    color: var(--color-primary);
  }

  h2 {
    font-family: var(--font-serif);
    font-size: 2rem;
    font-weight: var(--font-weight-semibold);
    margin-bottom: 0.75rem;
    line-height: 1.2;
  }

  h3 {
    font-family: var(--font-serif);
    font-size: 1.5rem;
    font-weight: var(--font-weight-normal);
    margin-bottom: 0.5rem;
    line-height: 1.3;
  }

  p {
    margin-bottom: var(--space-sm);
    line-height: 1.6;
  }

  a {
    color: var(--color-accent);
    text-decoration: none;
    transition: color var(--transition-speed) ease;
  }

  a:hover {
    color: var(--color-accent);
  }

  button {
    cursor: pointer;
    font-family: inherit;
    transition: all var(--transition-speed) ease;
    
    &:focus {
      outline: 2px solid var(--color-accent);
      outline-offset: 2px;
    }
    
    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }
  
  /* Focus management */
  *:focus {
    outline: 2px solid var(--color-accent);
    outline-offset: 2px;
  }
  
  /* Remove default focus for mouse users */
  .js-focus-visible *:focus:not(.focus-visible) {
    outline: none;
  }
  
  /* Smooth scrolling */
  html {
    scroll-behavior: smooth;
  }
  
  /* Animation utilities */
  .fade-in {
    animation: fadeIn 0.3s ease-in-out;
  }
  
  .slide-up {
    animation: slideUp 0.3s ease-out;
  }
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes slideUp {
    from { 
      opacity: 0;
      transform: translateY(20px);
    }
    to { 
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  /* Loading skeleton animation */
  @keyframes skeleton {
    0% { background-position: -200px 0; }
    100% { background-position: calc(200px + 100%) 0; }
  }
  
  .skeleton {
    background: linear-gradient(90deg, var(--color-border-light) 25%, var(--color-border) 50%, var(--color-border-light) 75%);
    background-size: 200px 100%;
    animation: skeleton 1.5s infinite linear;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  input, textarea, select {
    font-family: inherit;
    font-size: var(--font-size-md);
    letter-spacing: 0;
  }

  ul {
    list-style: none;
  }

  /* Grid Layout Utility Classes */
  .grid {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: var(--space-md);
  }

  .container {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 var(--space-md);
  }

  @media (max-width: 768px) {
    .grid {
      grid-template-columns: repeat(6, 1fr);
      gap: var(--space-sm);
    }
    
    .container {
      padding: 0 var(--space-sm);
    }
  }
`;

export default GlobalStyles;
